import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import ErrorModal from "@/components/ErrorModal";
import { loginConsumer } from "@/api";
import { handleIncompleteRegistration } from "@/lib/handleIncompleteRegistration";
import { extractApiError } from "@/lib/extractApiError";
import { navigateToBranded } from "@/lib/navigateToBranded";
import { getUtms } from "@/lib/getUtms";

const loginSchema = z.object({
  email: z
    .string()
    .min(1, "Bitte gib deine E-Mail-Adresse ein.")
    .email("Bitte gib eine gültige E-Mail-Adresse ein."),
  password: z.string().min(1, "Bitte gib dein Passwort ein."),
});

// Survives a page reload/back-navigation within the same tab while a login
// request is still in flight on the server, so a resubmit can't race the
// original request for the same consumer row. sessionStorage is per-tab, so
// it intentionally does not guard across tabs/devices.
const LOGIN_LOCK_KEY = "loginRequestInFlight";
// Backend-measured /consumer/login response time as of 2026-09-15: AVG
// ~1.5-1.7s, MAX ~7.4s. 10s covers that observed tail with margin.
const LOGIN_LOCK_TTL_MS = 10000;

const getLoginLockRemainingMs = () => {
  try {
    const startedAt = Number(sessionStorage.getItem(LOGIN_LOCK_KEY));
    if (!startedAt) return 0;
    const remaining = LOGIN_LOCK_TTL_MS - (Date.now() - startedAt);
    if (remaining <= 0) {
      sessionStorage.removeItem(LOGIN_LOCK_KEY);
      return 0;
    }
    return remaining;
  } catch {
    return 0;
  }
};

const acquireLoginLock = () => {
  try {
    sessionStorage.setItem(LOGIN_LOCK_KEY, String(Date.now()));
  } catch {
    /* sessionStorage unavailable (e.g. private browsing) — degrade to no lock */
  }
};

const releaseLoginLock = () => {
  try {
    sessionStorage.removeItem(LOGIN_LOCK_KEY);
  } catch {
    /* ignore */
  }
};

const Login = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Guards against duplicate concurrent submits (e.g. Enter + click race, or a
  // second submit slipping in before React commits the disabled button to the
  // DOM). A ref is used instead of relying on `isLoading` alone because it is
  // updated synchronously, before the first re-render.
  const isSubmittingRef = useRef(false);
  const abortControllerRef = useRef(null);

  // Cancel any in-flight login request if the component unmounts mid-request.
  useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);

  // If the page was reloaded (or the user navigated back) while a previous
  // login was still pending on the server, keep the form locked for the
  // remainder of the TTL instead of allowing an immediate resubmit.
  useEffect(() => {
    const remaining = getLoginLockRemainingMs();
    if (remaining <= 0) return;

    isSubmittingRef.current = true;
    setIsLoading(true);

    const timer = setTimeout(() => {
      isSubmittingRef.current = false;
      setIsLoading(false);
    }, remaining);

    return () => clearTimeout(timer);
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = async (data) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    acquireLoginLock();

    abortControllerRef.current = new AbortController();

    setApiError(null);
    setIsLoading(true);

    const UTMs = getUtms();

    try {
      const result = await loginConsumer(
        {
          consumer: {
            emailAddress: data.email,
            password: data.password,
          },
          campaignId: sessionStorage.getItem("ls") || null,
          utm: UTMs?.utm_campaign || null,
        },
        { signal: abortControllerRef.current.signal }
      );

      const token = result.data.token.uuid;
      if (token) {
        if (window.dataLayer) {
          window.dataLayer.push({
            event: "login",
            user_login_source: sessionStorage.getItem("ls") || UTMs?.utm_campaign || "direct",
          });
        }
        navigateToBranded(token);
      } else {
        setApiError({ message: "Login fehlgeschlagen. Bitte versuche es erneut.", errorCode: null });
      }
    } catch (error) {
      if (axios.isCancel(error)) return;

      const errData = error.response?.data;
      if (errData?.data?.error === "error_registration_incomplete") {
        handleIncompleteRegistration(errData.data.details, navigate, true);
      } else if (errData?.data?.error === "error_unauthorized") {
        setApiError({ message: "Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.", errorCode: errData?.meta?.ts || null });
      } else {
        setApiError(extractApiError(errData));
      }
    } finally {
      releaseLoginLock();
      isSubmittingRef.current = false;
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-10 items-center w-full max-w-[720px] pt-[80px] pb-[48px] px-5">
      {/* Intro section */}
      <div className="flex flex-col gap-4 items-center w-full">
        <h1 className="text-black text-center text-[32px] md:text-[40px]">
          Willkommen bei Lucky Strike
        </h1>
        <p className="text-black text-center text-[18px] font-medium leading-[1.4]">
          Schön, dass du da bist.
        </p>
        <p className="text-black text-center text-[18px] leading-[1.4]">
          <span className="font-extrabold">
            Hast du bereits ein Passwort?
          </span>{" "}
          Dann melde dich an.
          <br />
          Falls nicht, registriere dich jetzt.
        </p>
        <Button
          type="button"
          className="w-full max-w-120 md:w-auto md:max-w-none md:h-[54px] md:px-[20px] md:py-[13px] md:text-[24px] md:rounded-[14px]"
          size="sm"
          onClick={() => { sessionStorage.removeItem("migration"); navigate("/register"); }}
        >
          Registrieren
        </Button>
      </div>

      {/* Login form */}
      <form
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="off"
        className="flex flex-col gap-6 items-center w-full max-w-[480px]"
      >
        {/* Email */}
        <div className="w-full">
          <Input
            type="email"
            label="E-Mail"
            placeholder="E-Mail-Adresse eingeben"
            autoComplete="off"
            className="border-black"
            {...register("email")}
          />
          {errors.email && (
            <p className="text-primary text-[13px] font-medium mt-1">
              {errors.email.message}
            </p>
          )}
        </div>

        {/* Password */}
        <div className="w-full">
          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              label="Passwort"
              placeholder="Passwort eingeben"
              autoComplete="off"
              {...register("password")}
            />
            <button
              type="button"
              className="absolute right-0 bottom-4 text-[#9e9e9e] hover:text-black transition-colors p-2"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff className="w-6 h-6" />
              ) : (
                <Eye className="w-6 h-6" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="text-primary text-[13px] font-medium mt-1">
              {errors.password.message}
            </p>
          )}
          <div className="flex justify-end mt-2">
            <a
              href="/reset-password"
              className="font-extrabold text-[18px] text-black uppercase hover:underline leading-[28px] min-h-[40px] flex items-center"
            >
              Passwort vergessen
            </a>
          </div>
        </div>

        <ErrorModal
          open={!!apiError}
          onClose={() => setApiError(null)}
          message={apiError?.message}
          errorCode={apiError?.errorCode}
        />

        {/* Submit Button */}
        <Button
          type="submit"
          className="w-full md:h-[54px] md:px-[20px] md:py-[13px] md:text-[24px] md:rounded-[14px]"
          size="sm"
          disabled={isLoading}
        >
          {isLoading ? "Wird geladen..." : "Anmelden"}
        </Button>
      </form>
    </div>
  );
};

export default Login;
